import React, { useMemo } from 'react';
import { prescripteursBasePath } from '@europrocurement/l2d-modules/modules/PrescriberClient/constants/paths';
import { useCurrentCollectionCtx } from '@europrocurement/l2d-modules/providers';
import { useFullLayout } from '@europrocurement/flexy-components/components/templates/FullLayout/FullLayoutContext';
import DetailOrderToolbar from '../DetailOrderToolbar/DetailOrderToolbar';
import DetailOrderTitle from '../DetailOrderTitle/DetailOrderTitle';

type DetailOrderHeaderProps = {
    displayDetailOrder?: boolean;
};
const DetailOrderHeader: React.FunctionComponent<DetailOrderHeaderProps> = function ({
    displayDetailOrder = true,
}) {
    const { collection, dossiers } = useCurrentCollectionCtx();
    const { setPageTitle, clearPageTitle } = useFullLayout();

    /**
     * Récupère la dénomination de société du premier dossier.
     * Même logique métier que pour societeRef.
     *
     * @returns La dénomination société du premier dossier, ou undefined si aucun dossier valide
     */
    const societeDenomination = useMemo(() => {
        if (!dossiers || dossiers.length === 0) return undefined;

        const uniqueDenominations = [
            ...new Set(
                dossiers
                    .map((dossier) => dossier.societeDenomination?.trim())
                    .filter((d): d is string => Boolean(d)),
            ),
        ];

        return uniqueDenominations.length > 0 ? uniqueDenominations.join(' - ') : undefined;
    }, [dossiers]);

    const tabTitle = useMemo(() => {
        if (!collection?.numero) return undefined;
        if (societeDenomination) return `Commande #${collection.numero} | ${societeDenomination}`;
        return `Commande #${collection.numero}`;
    }, [collection?.numero, societeDenomination]);

    React.useEffect(() => {
        if (!tabTitle) return undefined;
        setPageTitle(tabTitle);
        return () => clearPageTitle();
    }, [tabTitle, setPageTitle, clearPageTitle]);

    /**
     * Détermine le statut global de la commande basé sur les statuts des dossiers.
     */
    const orderStatus = useMemo(() => {
        if (dossiers) {
            const statuts = dossiers
                .map((dossier) => dossier.statutLibelle)
                .filter((statut): statut is string => Boolean(statut));

            if (statuts.length === 0) {
                return 'Indéterminé';
            }

            // Si tous les dossiers ont le même statut
            const uniqueStatuts = [...new Set(statuts)];
            if (uniqueStatuts.length === 1) {
                return uniqueStatuts[0];
            }

            // Si statuts mixtes, retourner un statut composite ou le premier
            return 'En cours'; // ou statuts[0] selon la logique métier souhaitée
        }
        return 'Indéterminé';
    }, [dossiers]);

    const prescripteurInfo = useMemo(() => {
        if (!dossiers || dossiers.length === 0 || !collection) {
            return undefined;
        }

        const firstDossier = dossiers[0];
        return {
            id: collection?.prescripteurId || 0,
            nom: firstDossier.prescripteurLibelle || 'Non défini',
            codeComptable: firstDossier.prescripteurCodeCompta || 'N/A',
        };
    }, [collection, dossiers]);

    /**
     * Récupère les informations de l'offre.
     */
    const offerInfo = useMemo(() => {
        if (dossiers && dossiers.length > 0) {
            const firstDossier = dossiers[0];

            const id = firstDossier.offreId;
            const libelle = firstDossier.offreLibelle;

            return {
                id: id || 0,
                libelle: libelle || 'Non défini',
            };
        }
        return undefined;
    }, [dossiers]);

    const prescripteurDetailUrl = useMemo(() => {
        if (!prescripteursBasePath || !prescripteurInfo?.id) return undefined;
        return `/${prescripteursBasePath}/liste/${prescripteurInfo.id}`;
    }, [prescripteurInfo?.id]);

    if (!collection) {
        return null;
    }

    return (
        <>
            <DetailOrderToolbar
                id={collection.id as number}
                reference={collection.numero as string}
            />
            {displayDetailOrder && (
                <DetailOrderTitle
                    color="primary"
                    icon="folder-tree"
                    title={societeDenomination || `Commande #${collection.numero}`}
                    orderStatus={orderStatus}
                    prescripteur={prescripteurInfo}
                    prescripteurDetailUrl={prescripteurDetailUrl}
                    offre={offerInfo}
                />
            )}
        </>
    );
};

export default DetailOrderHeader;
